import { defineStore } from 'pinia'
import { gamificationService } from '@/services/api/gamificationService'

export const useGamificationStore = defineStore('gamification', {
  state: () => ({
    features: {
      roulette: false,
      prisoner_dilemma: false,
      smart_margin: false,
      food_tinder: false,
      battle_royale: false,
      ai_chatbot: false,
      lucky_wheel: false,
      quiz_battle: false,
    },
    featuresLoaded: false,

    // Roulette
    rouletteGame: null,
    rouletteLoading: false,

    // Smart Margin
    upsellRecommendation: null,
    upsellLoading: false,

    // Prisoner's Dilemma
    activeDeal: null,

    // Tinder for Food (KI-Fragen)
    swipeSession: null,
    swipeQuestions: [],
    swipeAnswers: [],
    swipeRecommendation: null,
    swipeLoading: false,

    // Battle Royale
    activeDrop: null,
    claimResult: null,
    battleRoyaleLoading: false,

    // AI Chat
    chatMessages: [],
    chatLoading: false,

    // Guest Profile
    guestProfile: null,

    // Lucky Wheel (Solo-Spiel)
    luckyWheelResult: null,
    luckyWheelSpinning: false,

    // Quiz Battle
    activeQuiz: null,
    quizScore: 0,
    quizLoading: false,

    // Shared Cart
    sharedCartItems: [],
    mySessionIdentifier: null,
  }),

  getters: {
    isRouletteActive: (state) => state.rouletteGame?.status === 'waiting' || state.rouletteGame?.status === 'spinning',
    isDropActive: (state) => state.activeDrop && new Date(state.activeDrop.endsAt) > new Date(),
    dropTimeRemaining: (state) => {
      if (!state.activeDrop) return 0
      const end = new Date(state.activeDrop.endsAt).getTime()
      return Math.max(0, Math.floor((end - Date.now()) / 1000))
    },
    myCartItems: (state) => {
      if (!state.mySessionIdentifier) return state.sharedCartItems
      return state.sharedCartItems.filter(item => item.sessionIdentifier === state.mySessionIdentifier)
    },
    myCartTotal: (state) => {
      const myItems = state.mySessionIdentifier
        ? state.sharedCartItems.filter(item => item.sessionIdentifier === state.mySessionIdentifier)
        : state.sharedCartItems
      return myItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0)
    },
    othersCartItems: (state) => {
      if (!state.mySessionIdentifier) return []
      return state.sharedCartItems.filter(item => item.sessionIdentifier !== state.mySessionIdentifier)
    },
  },

  actions: {
    resetGameState() {
      this.rouletteGame = null
      this.rouletteLoading = false
      this.upsellRecommendation = null
      this.activeDeal = null
      this.swipeSession = null
      this.swipeQuestions = []
      this.swipeAnswers = []
      this.swipeRecommendation = null
      this.activeDrop = null
      this.claimResult = null
      this.chatMessages = []
      this.luckyWheelResult = null
      this.activeQuiz = null
      this.quizScore = 0
      this.sharedCartItems = []
    },

    // ─── Feature Flags ──────────────────────────────────
    async loadFeatures() {
      try {
        const { data } = await gamificationService.getFeatureStatus()
        this.features = {
          roulette: data.roulette ?? false,
          prisoner_dilemma: data.prisonerDilemma ?? false,
          smart_margin: data.smartMargin ?? false,
          food_tinder: data.foodTinder ?? false,
          battle_royale: data.battleRoyale ?? false,
          ai_chatbot: data.aiChatbot ?? false,
          lucky_wheel: data.luckyWheel ?? false,
          quiz_battle: data.quizBattle ?? false,
        }
        this.featuresLoaded = true
      } catch (e) {
        console.error('Failed to load feature flags:', e)
      }
    },

    // ─── Roulette ───────────────────────────────────────
    async createRouletteGame(orderSessionId, hostIdentifier, hostDisplayName, avatarType, avatarColor, avatarIcon) {
      this.rouletteLoading = true
      try {
        const { data } = await gamificationService.createRouletteGame({
          orderSessionId, hostIdentifier, hostDisplayName, avatarType, avatarColor, avatarIcon
        })
        this.rouletteGame = data
        return data
      } finally {
        this.rouletteLoading = false
      }
    },

    async joinRouletteGame(gameId, sessionIdentifier, displayName, avatarType, avatarColor, avatarIcon) {
      const { data } = await gamificationService.joinRouletteGame(gameId, {
        sessionIdentifier, displayName, avatarType, avatarColor, avatarIcon
      })
      try {
        const { data: fullGame } = await gamificationService.getRouletteGame(gameId)
        if (fullGame) this.rouletteGame = fullGame
      } catch (e) { /* polling will catch up */ }
      return data
    },

    async spinRoulette(gameId, hostIdentifier) {
      const { data } = await gamificationService.spinRoulette(gameId, { hostIdentifier })
      this.rouletteGame = data
      return data
    },

    async finishRoulette(gameId) {
      const { data } = await gamificationService.finishRoulette(gameId)
      this.rouletteGame = data
      return data
    },

    async loadActiveRoulette(sessionId) {
      try {
        const { data } = await gamificationService.getActiveRouletteForSession(sessionId)
        this.rouletteGame = data
      } catch (e) {
        this.rouletteGame = null
      }
    },

    updateRouletteFromEvent(gameData) {
      this.rouletteGame = gameData
    },

    // ─── Smart Margin Upsell ────────────────────────────
    async loadSmartUpsell(productIds) {
      if (!this.features.smart_margin) return
      this.upsellLoading = true
      try {
        const { data } = await gamificationService.getSmartUpsell(productIds)
        this.upsellRecommendation = data
      } catch (e) {
        this.upsellRecommendation = null
      } finally {
        this.upsellLoading = false
      }
    },

    dismissUpsell() {
      this.upsellRecommendation = null
    },

    // ─── Prisoner's Dilemma ─────────────────────────────
    async checkPrisonerDilemma(tableId) {
      if (!this.features.prisoner_dilemma) return
      try {
        const { data } = await gamificationService.checkPrisonerDilemma(tableId)
        this.activeDeal = data
      } catch (e) {
        this.activeDeal = null
      }
    },

    setDealFromEvent(dealData) {
      this.activeDeal = dealData
    },

    dismissDeal() {
      this.activeDeal = null
    },

    // ─── Tinder for Food (KI-Fragen) ────────────────────
    async startFoodSwipe(orderSessionId, sessionIdentifier, guestProfileId) {
      if (!this.features.food_tinder) return
      this.swipeLoading = true
      try {
        const { data } = await gamificationService.startFoodSwipe({
          orderSessionId, sessionIdentifier, guestProfileId
        })
        this.swipeSession = data.swipeSession
        this.swipeQuestions = data.questions || data.deck || []
        return data
      } finally {
        this.swipeLoading = false
      }
    },

    async submitSwipeResults(answers) {
      if (!this.swipeSession) return
      this.swipeLoading = true
      try {
        const { data } = await gamificationService.submitSwipeResults(
          this.swipeSession.id, answers.liked || [], answers.disliked || []
        )
        this.swipeRecommendation = data
        return data
      } finally {
        this.swipeLoading = false
      }
    },

    resetSwipe() {
      this.swipeSession = null
      this.swipeQuestions = []
      this.swipeAnswers = []
      this.swipeRecommendation = null
    },

    // ─── Lucky Wheel (Solo-Spiel) ───────────────────────
    async spinLuckyWheel(orderSessionId, sessionIdentifier) {
      this.luckyWheelSpinning = true
      try {
        const { data } = await gamificationService.spinLuckyWheel({ orderSessionId, sessionIdentifier })
        this.luckyWheelResult = data
        return data
      } finally {
        this.luckyWheelSpinning = false
      }
    },

    dismissLuckyWheelResult() {
      this.luckyWheelResult = null
    },

    // ─── Quiz Battle ────────────────────────────────────
    async startQuiz(orderSessionId, sessionIdentifier, mode = 'solo') {
      this.quizLoading = true
      this.quizScore = 0
      try {
        const { data } = await gamificationService.startQuiz({ orderSessionId, sessionIdentifier, mode })
        this.activeQuiz = data
        return data
      } finally {
        this.quizLoading = false
      }
    },

    async answerQuiz(quizId, questionIndex, answerIndex) {
      const { data } = await gamificationService.answerQuiz(quizId, { questionIndex, answerIndex })
      if (data.correct) {
        this.quizScore += data.points || 10
      }
      if (data.finished) {
        this.activeQuiz = { ...this.activeQuiz, ...data }
      }
      return data
    },

    resetQuiz() {
      this.activeQuiz = null
      this.quizScore = 0
    },

    // ─── Battle Royale ──────────────────────────────────
    async loadActiveDrop() {
      if (!this.features.battle_royale && !this.features.battleRoyale) return
      try {
        const { data } = await gamificationService.getActiveDrop()
        this.activeDrop = (data && typeof data === 'object' && !Array.isArray(data) && data.id) ? data : null
      } catch (e) {
        this.activeDrop = null
      }
    },

    async claimDrop(dropId, orderSessionId, sessionIdentifier) {
      this.battleRoyaleLoading = true
      try {
        const { data } = await gamificationService.claimDrop(dropId, { orderSessionId, sessionIdentifier })
        this.claimResult = data
        return data
      } catch (e) {
        if (e.response?.status === 410) {
          this.claimResult = { failed: true, consolation: e.response.data.consolation, message: e.response.data.message }
        } else if (e.response?.status === 409) {
          this.claimResult = { alreadyClaimed: true, message: e.response.data.message }
        }
        throw e
      } finally {
        this.battleRoyaleLoading = false
      }
    },

    updateDropFromEvent(dropData, action) {
      if (action === 'started') this.activeDrop = dropData
      else if (action === 'claimed' && this.activeDrop?.id === dropData.id) this.activeDrop = dropData
      else if (action === 'ended' && this.activeDrop?.id === dropData.id) this.activeDrop = null
    },

    dismissClaimResult() {
      this.claimResult = null
    },

    // ─── AI Chat ────────────────────────────────────────
    async sendMessage(orderSessionId, message, guestProfileId) {
      this.chatLoading = true
      this.chatMessages.push({ role: 'user', content: message, createdAt: new Date().toISOString() })
      try {
        const payload = { message }
        if (orderSessionId) payload.orderSessionId = orderSessionId
        if (guestProfileId) payload.guestProfileId = guestProfileId
        const { data } = await gamificationService.sendChatMessage(payload)
        this.chatMessages.push({ role: 'assistant', content: data.content || data.message || 'Keine Antwort erhalten.', metadata: data.metadata, createdAt: new Date().toISOString() })
        return data
      } catch (e) {
        this.chatMessages.push({ role: 'assistant', content: 'Entschuldigung, es gab einen Fehler. Bitte versuche es erneut.', createdAt: new Date().toISOString() })
        throw e
      } finally {
        this.chatLoading = false
      }
    },

    async loadChatHistory(sessionId) {
      try {
        const { data } = await gamificationService.getChatHistory(sessionId)
        this.chatMessages = data
      } catch (e) { this.chatMessages = [] }
    },

    clearChat() { this.chatMessages = [] },

    // ─── Guest Profile ──────────────────────────────────
    async loadOrCreateProfile(deviceFingerprint, profileData) {
      try {
        const { data } = await gamificationService.getGuestProfile(deviceFingerprint)
        if (data) { this.guestProfile = data; return data }
      } catch (e) { /* no profile */ }
      if (profileData) {
        const { data } = await gamificationService.createGuestProfile({ deviceFingerprint, ...profileData })
        this.guestProfile = data
        return data
      }
      return null
    },

    async saveProfile(profileData) {
      const { data } = await gamificationService.createGuestProfile(profileData)
      this.guestProfile = data
      return data
    },

    // ─── Shared Cart ────────────────────────────────────
    setMySessionIdentifier(identifier) {
      this.mySessionIdentifier = identifier
    },

    updateSharedCart(items) {
      this.sharedCartItems = items
    },
  }
})
